import React, { useCallback, useState } from 'react';
import useIntl from '@afosto/utils-react/intl/useIntl';
import usePrivate from '@afosto/auth-react/usePrivate';
import useSubscriptions from '@afosto/utils-react/subscription/useSubscriptions';
import { useConfirm } from '@afosto/app-components-react/ConfirmDialog';
import { useHistory } from 'react-router';
import { useOrderContext } from '../../../components/OrderProvider';
import { useSnackbar } from 'notistack';
import OrderItemsGrid from '../../../components/OrderItemsGrid';
import OrderTypeDialog from '../../../components/OrderTypeDialog';
import RoutesDialog from './RoutesDialog';
import BulkButton from '@afosto/components-react/BulkButton';
import { items as itemsApi } from '@afosto/order/api/Items';
import { orders as ordersApi } from '@afosto/order/api/Orders';
import translations from './translations';

const OrderItems = () => {
  usePrivate();

  const confirm = useConfirm();
  const intl = useIntl();
  const history = useHistory();
  const subscriptions = useSubscriptions();
  const { order, calculation, previewContainer } = useOrderContext();
  const { enqueueSnackbar } = useSnackbar();
  const [items, setItems] = useState([]);
  const [showOrderTypeDialog, setShowOrderTypeDialog] = useState(false);
  const [showChangeRoutesDialog, setShowChangeRoutesDialog] = useState(false);
  const [shouldReloadItems, setShouldReloadItems] = useState(false);
  const hasWmsSubscription = subscriptions.some(subscription => subscription.code === 'wms');

  const handleCreateOrder = async type => {
    try {
      const orderResponse = await ordersApi.createStack({
        ...(type !== 'purchase'
          ? {
              client_id: order?.client_id ?? null,
              settings: {
                contact: {
                  id: order?.settings?.contact?.id ?? null,
                },
                organisation: {
                  id: order?.settings?.organisation?.id ?? null,
                },
                billing: {
                  address_id: order?.settings?.billing?.address_id ?? null,
                },
                shipping: {
                  address_id: order?.settings?.shipping?.address_id ?? null,
                },
              },
            }
          : {}),
        type,
      });
      const { data: orderData = null } = orderResponse;

      const inboundTypes = ['return', 'cancellation', 'purchase'];
      await itemsApi.addItems(
        orderData?.id,
        items.map(item => ({
          sku: item.sku,
          reference: item.reference,
          description: item.description,
          quantity: Math.abs(item.quantity),
          is_inbound: inboundTypes.includes(type),
          is_backorder: type === 'purchase',
          item_ids: item?.ids ?? [],
          source_order_id: type === 'purchase' ? order?.id : null,
        })),
      );

      history.push(`/orders/edit/${orderData.id}`);
    } catch (err) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.orderNotCreated), {
        variant: 'error',
      });
    }
  };

  const handleRouteChange = () => {
    setShouldReloadItems(true);
    setShowChangeRoutesDialog(false);
  };

  const handleCancelAllocation = async () => {
    try {
      const confirmResponse = await confirm({
        title: intl.formatMessage(translations.confirmCancelAllocation.title),
        description: intl.formatMessage(translations.confirmCancelAllocation.description),
        catchOnCancel: false,
        buttons: [
          {
            label: intl.formatMessage(translations.confirmCancelAllocation.buttons.cancel),
            type: 'cancel',
            variant: 'outlined',
            style: {
              marginRight: 'auto',
            },
          },
          {
            label: intl.formatMessage(translations.confirmCancelAllocation.buttons.continue),
            response: 'cancelAllocation',
            type: 'submit',
            variant: 'contained',
            color: 'primary',
          },
        ],
      });

      if (confirmResponse !== 'cancelAllocation') {
        return;
      }

      const orderItemIds = items
        .filter(item => item.isAvailable && !item.shipments?.length)
        .reduce((acc, item) => [...acc, ...(item?.ids ?? [])], []);

      if (orderItemIds.length > 0) {
        await itemsApi.cancelOrderItems(order?.id, orderItemIds);

        enqueueSnackbar(intl.formatMessage(translations.feedback.itemAllocationCancelled), {
          variant: 'success',
        });
      }

      setShouldReloadItems(true);
    } catch (err) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.itemAllocationNotCancelled), {
        variant: 'error',
      });
    }
  };

  const handleAddSelection = useCallback(selection => {
    setItems(oldItems => {
      const currentItemIds = oldItems.map(item => item.id);
      const newItems = selection.filter(item => !currentItemIds.includes(item.id));

      return [...oldItems, ...newItems];
    });
  }, []);

  const handleRemoveSelection = useCallback(selection => {
    const removedIds = selection.map(item => item.id);
    setItems(oldItems => oldItems.filter(item => !removedIds.includes(item.id)));
  }, []);

  const bulkActions = [
    {
      key: 'createOrder',
      render: () => (
        <BulkButton variant="outlined" size="small" onClick={() => setShowOrderTypeDialog(true)}>
          {intl.formatMessage(translations.actions.createOrder)}
        </BulkButton>
      ),
    },
    ...(hasWmsSubscription
      ? [
          {
            key: 'changeRoute',
            render: () => (
              <BulkButton
                variant="outlined"
                size="small"
                onClick={() => setShowChangeRoutesDialog(true)}
              >
                {intl.formatMessage(translations.actions.changeRoute)}
              </BulkButton>
            ),
          },
          {
            key: 'cancelAllocation',
            render: () => (
              <BulkButton variant="outlined" size="small" onClick={handleCancelAllocation}>
                {intl.formatMessage(translations.actions.cancelAllocation)}
              </BulkButton>
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      <OrderItemsGrid
        order={order}
        calculation={calculation}
        modules={['selection']}
        bulkActions={bulkActions}
        onAddSelection={handleAddSelection}
        onRemoveSelection={handleRemoveSelection}
        previewContainer={previewContainer}
        shouldReloadItems={shouldReloadItems}
        setShouldReloadItems={setShouldReloadItems}
      />
      <OrderTypeDialog
        isOpen={showOrderTypeDialog}
        onClose={() => setShowOrderTypeDialog(false)}
        onSelect={handleCreateOrder}
        type="return"
      />
      <RoutesDialog
        isOpen={showChangeRoutesDialog}
        items={items}
        onClose={() => setShowChangeRoutesDialog(false)}
        onSave={handleRouteChange}
        order={order}
      />
    </>
  );
};

export default OrderItems;
